import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Insert, type Row } from '@/lib/supabase'

export type Venue = Row<'venues'>

/**
 * The venue library.
 *
 * This table is what stands in for Google Places Autocomplete and the Routes
 * API. A venue is measured once — by hand, or by asking Claude Code to look it
 * up — and every wedding there afterwards inherits the distance for free.
 *
 * Which means the single most important thing this screen does is make an
 * unmeasured venue obvious, because an unmeasured venue is a quote with no
 * travel on it.
 */

export const venueKeys = {
  list: ['venues'] as const,
  detail: (id: string) => ['venue', id] as const,
  usage: (id: string) => ['venue-usage', id] as const,
}

export function useVenues(includeArchived = false) {
  return useQuery({
    queryKey: [...venueKeys.list, includeArchived],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Venue[]> => {
      let query = supabase.from('venues').select('*').order('name')
      if (!includeArchived) query = query.is('archived_at', null)

      const { data, error } = await query
      if (error) throw error
      return data
    },
  })
}

export function useVenue(id: string | undefined) {
  return useQuery({
    queryKey: venueKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<Venue> => {
      const { data, error } = await supabase.from('venues').select('*').eq('id', id ?? '').single()
      if (error) throw error
      return data
    },
  })
}

/** How many weddings reference this venue — shown before archiving one. */
export function useVenueUsage(id: string | undefined) {
  return useQuery({
    queryKey: venueKeys.usage(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('jobs')
        .select('id', { count: 'exact', head: true })
        .eq('venue_id', id ?? '')

      if (error) throw error
      return count ?? 0
    },
  })
}

export function useSaveVenue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string | undefined; values: Insert<'venues'> }): Promise<Venue> => {
      const query = id
        ? supabase.from('venues').update(values).eq('id', id).select('*').single()
        : supabase.from('venues').insert(values).select('*').single()

      const { data, error } = await query
      if (error) throw error
      return data
    },
    onSuccess: (venue) => {
      queryClient.setQueryData(venueKeys.detail(venue.id), venue)
      void queryClient.invalidateQueries({ queryKey: venueKeys.list })
    },
    onError: (error) => {
      toast.error('Could not save the venue', { description: describeError(error) })
    },
  })
}

export function useArchiveVenue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from('venues')
        .update({ archived_at: archived ? new Date().toISOString() : null })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.list })
    },
    onError: (error) => {
      toast.error('Could not archive the venue', { description: describeError(error) })
    },
  })
}

/** A venue with no distance cannot price travel. This is the count she should see. */
export function unmeasuredCount(venues: Venue[] | undefined): number {
  return (venues ?? []).filter((venue) => venue.distance_km === null).length
}
